import { useEffect, useRef } from 'react';

import { useAppSelector } from '@/store';

import type { TaskTabType } from '@/types';

/**
 * Auto-refresh hook that triggers a callback at the configured interval.
 * Instead of auto-refreshing task lists directly, it shows the refresh bubble.
 */
export function useAutoRefresh(
  onRefresh: () => void,
  activeTab: TaskTabType,
  onShowRefreshBubble: () => void
) {
  const settings = useAppSelector((s) => s.global.settings);
  const savedCallback = useRef(onRefresh);
  const savedBubble = useRef(onShowRefreshBubble);

  useEffect(() => {
    savedCallback.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    savedBubble.current = onShowRefreshBubble;
  }, [onShowRefreshBubble]);

  useEffect(() => {
    const timeoutSec = Number(settings?.AUTO_REFRESH_TIMEOUT ?? '0');
    if (timeoutSec <= 0) return;

    const interval = setInterval(() => {
      savedCallback.current();
      if (activeTab === 'upcoming') {
        savedBubble.current();
      }
    }, timeoutSec * 1000);

    return () => {
      clearInterval(interval);
    };
  }, [settings, activeTab]);
}
